import { useTranslation } from 'react-i18next'
import ASelect from '../A/ASelect/ASelect'
import { I18N_LOCALES } from '@/i18n/script/consts'
import styles from './LanguageSwitcher.module.less'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import MiddleText from '../MiddleText/MiddleText'
import useIsMobile from '@/helpers/commonHooks/useIsMobile'
import { useClickAway, useToggle } from 'ahooks'
import AIcon from '../A/AIcon/AIcon'
import { INTERNAL_HOST } from '@/commonConst'

const useLanguage = () => {
  const { i18n } = useTranslation()
  const [storageLanguage, setStorageLanguage] = useTypedLocalStorage('adeff-language', {
    defaultValue: '',
  })
  const [value, setValue] = useState(storageLanguage)

  const setLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    setValue(i18n.language)
    setStorageLanguage(i18n.language)
  }

  useEffect(() => {
    let lng = storageLanguage
    if (!lng) {
      lng = INTERNAL_HOST.includes(location.hostname) ? 'zh' : 'en'
    }
    setTimeout(() => {
      setLanguage(lng)
    })
  }, [storageLanguage])

  return { language: value, setLanguage }
}

interface LanguageSwitcherProps {
  className?: string
  active?: boolean
}

function LanguageSwitcher(props: LanguageSwitcherProps) {
  const { t } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const isMobile = useIsMobile()

  const options: Array<{ label: string; value: string; nativeName: string }> = [
    {
      name: 'English',
      key: 'en',
      nativeName: 'English',
    },
    {
      name: 'Chinese',
      key: 'zh',
      nativeName: '简体中文',
    },
    {
      name: 'Traditional Chinese',
      key: 'zh-Hant',
      nativeName: '繁体中文',
    },
    ...I18N_LOCALES,
  ].map((locale) => {
    return {
      label: locale.name,
      value: locale.key,
      nativeName: locale.nativeName,
    }
  })

  return (
    isMobile
      ? <MobileLanguageSwitcher active={props.active} value={language} options={options}
        onChange={setLanguage}
      />
      : <ASelect
        testId='language-switcher-select'
        className={cls(styles.languageSwitcher, props.className)}
        dropdownStyle={{ width: 280 }}
        options={options}
        optionRender={(option) => {
          return (
            <span className={styles.languageSwitcherOption}>
              <p className={styles.languageSwitcherOptionNativeName}>{option.data.nativeName}</p>
              <p className={styles.languageSwitcherOptionLabel}>{t(`i18n.${option.data.label}`)} </p>
            </span>
          )
        }}
        defaultValue={language}
        value={language}
        labelRender={(option) => {
          const optionData = options.find((item) => item.value === option.value)
          return <MiddleText text={optionData?.nativeName || ''} />
        }}
        onChange={setLanguage}
      ></ASelect>
  )
}



interface MobileLanguageSwitcherProps {
  className?: string
  active?: boolean
  value: string | undefined
  options: Array<{ label: string; value: string; nativeName: string }>
  onChange: (value: string) => void
}

function MobileLanguageSwitcher(props: MobileLanguageSwitcherProps) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [open, { toggle }] = useToggle()
  useClickAway(() => {
    if (open) {
      toggle()
    }
  }, boxRef)

  const handleClick = (value: string) => {
    props.onChange(value)
    toggle()
  }
  const label = props.options.find(opt => opt.value === props.value)?.nativeName
  return <div className={styles.mobileLanguageSwitcher} ref={boxRef} data-testid='language-switcher-mobile'>
    <div className={cls(styles.label, props.active && styles.active)} onClick={toggle} data-testid='language-switcher-mobile-btn'>
      <span className={styles.labelText}>{label}</span>
      <AIcon type="icon-line-global_down" className={styles.labelIcon} />
    </div>
    <div className={cls(styles.options, { [styles.visible]: open })}>
      {props.options.map((option) => {
        return <div className={cls(styles.option, { [styles.active]: option.value === props.value })} key={option.value} data-testid={`language-switcher-mobile-option-${option.value}`} onClick={() => handleClick(option.value)}>{option.nativeName}</div>
      })}
    </div>
  </div>
}

export default LanguageSwitcher
